import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, CreditCard, LoaderCircle, RefreshCw, Tag } from 'lucide-react';
import {
  chargeWalletApi,
  getWalletPaymentsApi,
  getWalletTransactionsApi,
  validateWalletVoucherApi
} from '../../api/wallet.api';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Skeleton } from '../../components/ui/Skeleton';
import { Tabs } from '../../components/ui/Tabs';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppRedux';
import { useI18n } from '../../hooks/useI18n';
import { useToast } from '../../hooks/useToast';
import { fetchWalletBalance } from '../../redux/slices/walletSlice';
import { formatAmountDisplay, formatAmountInputFromRaw, parseAmountValue } from '../../utils/amountInput';
import { classNames } from '../../utils/classNames';
import { WalletSectionSkeleton } from './WalletSectionSkeleton';

const MIN_CHARGE_AMOUNT = 100000;
const QUICK_AMOUNTS = [100000, 200000, 500000];

const WalletColumn = ({ title, children, className }) => (
  <section
    className={classNames(
      'flex min-h-0 flex-col rounded-2xl border border-border bg-white/50 dark:bg-slate-950/15',
      className
    )}
  >
    {title ? (
      <header className="border-b border-border/80 px-4 py-3 md:px-5">
        <h3 className="text-sm font-semibold text-text">{title}</h3>
      </header>
    ) : null}
    <div className="flex min-h-0 flex-1 flex-col p-4 md:p-5">{children}</div>
  </section>
);

export const WalletSection = () => {
  const { t, formatNumber, locale, formatDate } = useI18n();
  const toast = useToast();
  const dispatch = useAppDispatch();
  const { balance, loading: balanceLoading, refreshing: balanceRefreshing } = useAppSelector((state) => state.wallet);

  const [payments, setPayments] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [activityTab, setActivityTab] = useState('payments');

  const [amountInput, setAmountInput] = useState('');
  const [voucherInput, setVoucherInput] = useState('');
  const [voucherPreview, setVoucherPreview] = useState(null);
  const [validatingVoucher, setValidatingVoucher] = useState(false);
  const [charging, setCharging] = useState(false);

  const amount = useMemo(() => parseAmountValue(amountInput), [amountInput]);
  const amountIsValid = amount >= MIN_CHARGE_AMOUNT;
  const hasVoucherCode = Boolean(voucherInput.trim());
  const needsVoucherValidation = hasVoucherCode && !voucherPreview?.canApply;

  const payableAmount = useMemo(() => {
    if (voucherPreview?.canApply) {
      return voucherPreview.payableAmount;
    }

    return amountIsValid ? amount : 0;
  }, [amount, amountIsValid, voucherPreview]);

  const formatMoney = useCallback(
    (value) => {
      const formatted = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(Number(value) || 0);
      return `${formatNumber(formatted)} ${t('walletCurrency')}`;
    },
    [formatNumber, locale, t]
  );

  const loadWalletHistory = useCallback(
    async ({ silent = false } = {}) => {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setLoadError('');

      try {
        const [paymentsResult, transactionsResult] = await Promise.all([
          getWalletPaymentsApi(),
          getWalletTransactionsApi()
        ]);

        setPayments(paymentsResult);
        setTransactions(transactionsResult);
      } catch (requestError) {
        const message = requestError?.message || t('walletLoadError');
        setLoadError(message);

        if (silent) {
          toast.error(message);
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [t, toast]
  );

  const loadWallet = useCallback(
    async ({ silent = false } = {}) => {
      await Promise.all([
        dispatch(fetchWalletBalance({ silent })),
        loadWalletHistory({ silent })
      ]);
    },
    [dispatch, loadWalletHistory]
  );

  useEffect(() => {
    loadWallet();
  }, [loadWallet]);

  useEffect(() => {
    setVoucherPreview(null);
  }, [amountInput, voucherInput]);

  const handleValidateVoucher = async () => {
    const code = voucherInput.trim();

    if (!code) {
      toast.error(t('walletVoucherRequired'));
      return;
    }

    if (!amountIsValid) {
      toast.error(t('walletAmountMin', { amount: formatMoney(MIN_CHARGE_AMOUNT) }));
      return;
    }

    setValidatingVoucher(true);
    setVoucherPreview(null);

    try {
      const result = await validateWalletVoucherApi({ code, amount });

      if (!result.canApply) {
        toast.error(result.reason || t('walletVoucherInvalid'));
        return;
      }

      setVoucherPreview(result);
    } catch (requestError) {
      toast.error(requestError?.message || t('walletVoucherInvalid'));
    } finally {
      setValidatingVoucher(false);
    }
  };

  const handleCharge = async () => {
    if (!amountIsValid) {
      toast.error(t('walletAmountMin', { amount: formatMoney(MIN_CHARGE_AMOUNT) }));
      return;
    }

    if (needsVoucherValidation) {
      toast.error(t('walletValidateVoucherFirst'));
      return;
    }

    setCharging(true);

    try {
      const result = await chargeWalletApi({
        amount,
        voucherCode: voucherInput.trim() || undefined
      });

      if (result.paymentUrl) {
        window.location.assign(result.paymentUrl);
        return;
      }

      toast.error(t('walletChargeError'));
    } catch (requestError) {
      toast.error(requestError?.message || t('walletChargeError'));
    } finally {
      setCharging(false);
    }
  };

  const normalizePaymentStatus = (status) => status?.toLowerCase?.() || '';

  const getPaymentStatusLabel = (status) => {
    const normalized = normalizePaymentStatus(status);

    if (normalized === 'paid') {
      return t('walletPaymentPaid');
    }

    if (normalized === 'unpaid') {
      return t('walletPaymentUnpaid');
    }

    if (normalized === 'cancelled' || normalized === 'canceled') {
      return t('walletPaymentCancelled');
    }

    return status || t('walletPaymentUnknown');
  };

  const getPaymentStatusBadgeClass = (status) => {
    const normalized = normalizePaymentStatus(status);

    if (normalized === 'paid') {
      return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300';
    }

    if (normalized === 'cancelled' || normalized === 'canceled') {
      return 'bg-slate-100 text-slate-600 dark:bg-slate-800/60 dark:text-slate-300';
    }

    return 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300';
  };

  const isCreditTransaction = (transactionType) =>
    transactionType === 'WalletTopUp' || transactionType?.toLowerCase?.().includes('topup');

  const activityItems = activityTab === 'payments' ? payments : transactions;
  const payDisabled = charging || !amountIsValid || needsVoucherValidation;

  const renderHistoryList = () => {
    if (refreshing || balanceRefreshing) {
      return (
        <ul className="space-y-2" aria-busy="true">
          {[0, 1, 2, 3].map((item) => (
            <li key={item} className="rounded-lg border border-border/60 px-3 py-2.5">
              <Skeleton className="h-4 w-2/5" />
              <Skeleton className="mt-2 h-3 w-1/3" />
            </li>
          ))}
        </ul>
      );
    }

    if (activityItems.length === 0) {
      return (
        <p className="py-10 text-center text-sm text-muted">
          {activityTab === 'payments' ? t('walletPaymentsEmpty') : t('walletTransactionsEmpty')}
        </p>
      );
    }

    if (activityTab === 'payments') {
      return (
        <ul className="divide-y divide-border">
          {payments.map((payment) => (
            <li key={payment.id} className="flex items-center justify-between gap-3 py-3 text-sm first:pt-0">
              <div className="min-w-0">
                <p className="truncate font-medium text-text">
                  {payment.description || payment.title || t('walletPaymentUnknown')}
                </p>
                <p className="mt-0.5 text-xs text-muted">
                  {payment.createdAt
                    ? formatDate(payment.createdAt, { dateStyle: 'medium', timeStyle: 'short' })
                    : '--'}
                </p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1">
                <p className="font-semibold tabular-nums text-text">{formatMoney(payment.payableAmount)}</p>
                <span
                  className={classNames(
                    'rounded-full px-2 py-0.5 text-[11px] font-semibold',
                    getPaymentStatusBadgeClass(payment.status)
                  )}
                >
                  {getPaymentStatusLabel(payment.status)}
                </span>
              </div>
            </li>
          ))}
        </ul>
      );
    }

    return (
      <ul className="divide-y divide-border">
        {transactions.map((transaction) => {
          const isCredit = isCreditTransaction(transaction.transactionType);

          return (
            <li key={transaction.id} className="flex items-center justify-between gap-3 py-3 text-sm first:pt-0">
              <div className="min-w-0">
                <p className="truncate font-medium text-text">
                  {transaction.description ||
                    transaction.transactionTypeDescription ||
                    transaction.transactionType ||
                    t('walletTransactionUnknown')}
                </p>
                <p className="mt-0.5 text-xs text-muted">
                  {transaction.createdAt
                    ? formatDate(transaction.createdAt, { dateStyle: 'medium', timeStyle: 'short' })
                    : '--'}
                </p>
              </div>
              <span
                className={classNames(
                  'shrink-0 font-bold tabular-nums',
                  isCredit ? 'text-emerald-600 dark:text-emerald-300' : 'text-rose-600 dark:text-rose-300'
                )}
              >
                {formatMoney(transaction.amount)}
              </span>
            </li>
          );
        })}
      </ul>
    );
  };

  if (loading || balanceLoading) {
    return <WalletSectionSkeleton />;
  }

  if (loadError && balance === null) {
    return (
      <div
        role="alert"
        className="rounded-2xl border border-rose-200/80 bg-rose-50/80 px-4 py-6 text-center dark:border-rose-500/20 dark:bg-rose-500/10"
      >
        <p className="text-sm text-rose-800 dark:text-rose-100">{loadError}</p>
        <Button variant="secondary" className="mt-4" onClick={() => loadWallet()}>
          {t('walletRetry')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4 rounded-2xl border border-border bg-accent/5 px-4 py-4 md:px-5">
        <div>
          <p className="text-xs font-medium text-muted">{t('walletBalanceLabel')}</p>
          {balanceRefreshing ? (
            <Skeleton className="mt-2 h-9 w-44 rounded-lg" />
          ) : (
            <p className="mt-1 text-2xl font-bold tracking-tight text-text">
              {balance === null ? '--' : formatMoney(balance)}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => loadWallet({ silent: true })}
          disabled={balanceRefreshing || refreshing}
          className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-white/80 px-3 py-2 text-xs font-semibold text-text transition hover:border-accent/40 disabled:opacity-50 dark:bg-slate-950/40"
          aria-label={t('walletRefresh')}
        >
          <RefreshCw size={14} className={classNames((balanceRefreshing || refreshing) && 'animate-spin')} aria-hidden />
          {t('walletRefresh')}
        </button>
      </div>

      <div className="grid gap-5 lg:grid-cols-2 lg:items-stretch">
        <WalletColumn title={t('walletChargeTitle')} className="order-1">
          <div className="flex flex-1 flex-col gap-4">
            <div>
              <p className="mb-2 text-xs font-medium text-muted">{t('walletQuickAmounts')}</p>
              <div className="grid grid-cols-3 gap-2">
                {QUICK_AMOUNTS.map((quickAmount) => (
                  <button
                    key={quickAmount}
                    type="button"
                    onClick={() => setAmountInput(formatAmountDisplay(quickAmount))}
                    disabled={charging}
                    className={classNames(
                      'rounded-xl border px-2 py-2.5 text-xs font-semibold transition',
                      amount === quickAmount
                        ? 'border-accent bg-accent text-white dark:text-slate-950'
                        : 'border-border bg-white/80 text-text hover:border-accent/40 dark:bg-slate-950/30'
                    )}
                  >
                    {formatMoney(quickAmount)}
                  </button>
                ))}
              </div>
            </div>

            <Input
              label={t('walletAmountLabel')}
              inputMode="numeric"
              placeholder={t('walletAmountPlaceholder')}
              value={amountInput}
              onChange={(event) => setAmountInput(formatAmountInputFromRaw(event.target.value))}
              disabled={charging}
              aria-invalid={Boolean(amountInput) && !amountIsValid}
            />
            <p
              className={classNames(
                '-mt-2 text-xs',
                amountInput && !amountIsValid ? 'text-amber-600 dark:text-amber-300' : 'text-muted'
              )}
            >
              {t('walletAmountHelper', { amount: formatMoney(MIN_CHARGE_AMOUNT) })}
            </p>

            <div className="space-y-2">
              <span className="text-sm font-medium text-muted">{t('walletVoucherLabel')}</span>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                <div className="min-w-0 flex-1">
                  <input
                    type="text"
                    placeholder={t('walletVoucherPlaceholder')}
                    value={voucherInput}
                    onChange={(event) => setVoucherInput(event.target.value)}
                    disabled={charging || validatingVoucher}
                    className="w-full rounded-2xl border border-border bg-white/80 px-4 py-3 text-base text-text outline-none placeholder:text-muted/80 focus:border-accent md:text-sm dark:bg-panel-strong"
                  />
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  className="shrink-0 sm:min-w-[7.5rem]"
                  onClick={handleValidateVoucher}
                  disabled={charging || validatingVoucher || !hasVoucherCode || !amountIsValid}
                >
                  {validatingVoucher ? (
                    <LoaderCircle size={16} className="animate-spin" aria-hidden />
                  ) : (
                    <Tag size={16} aria-hidden />
                  )}
                  {t('walletValidateVoucher')}
                </Button>
              </div>
              {voucherPreview?.canApply ? (
                <p className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-300">
                  <CheckCircle2 size={14} aria-hidden />
                  {t('walletVoucherApplied')}
                  {voucherPreview.discountAmount > 0
                    ? ` · ${t('walletDiscountAmount')}: ${formatMoney(voucherPreview.discountAmount)}`
                    : ''}
                </p>
              ) : needsVoucherValidation ? (
                <p className="text-xs text-amber-600 dark:text-amber-300">{t('walletValidateVoucherFirst')}</p>
              ) : null}
            </div>

            <div className="mt-auto space-y-3 border-t border-border pt-4">
              <div
                className={classNames(
                  'flex items-center justify-between gap-3 rounded-xl px-3 py-2.5',
                  amountIsValid ? 'bg-accent/10' : 'bg-white/60 dark:bg-slate-950/25'
                )}
              >
                <span className="text-sm text-muted">{t('walletPayableAmount')}</span>
                <span className="text-lg font-bold text-text">
                  {amountIsValid ? formatMoney(payableAmount) : '--'}
                </span>
              </div>

              <Button
                className="w-full py-3 text-base font-bold"
                onClick={handleCharge}
                disabled={payDisabled}
              >
                {charging ? (
                  <>
                    <LoaderCircle size={18} className="animate-spin" aria-hidden />
                    {t('walletPayRedirecting')}
                  </>
                ) : (
                  <>
                    <CreditCard size={18} aria-hidden />
                    <span>
                      {t('walletPayButton')}
                      {amountIsValid ? ` · ${formatMoney(payableAmount)}` : ''}
                    </span>
                  </>
                )}
              </Button>
            </div>
          </div>
        </WalletColumn>

        <WalletColumn title={t('walletActivityTitle')} className="order-2">
          <Tabs
            value={activityTab}
            onChange={setActivityTab}
            className="mb-4 w-full"
            tabClassName="min-w-0 flex-1 px-3 text-xs"
            tabs={[
              { value: 'payments', label: t('walletPaymentsTitle') },
              { value: 'transactions', label: t('walletTransactionsTitle') }
            ]}
          />
          <div className="min-h-0 flex-1">
            {renderHistoryList()}
          </div>
        </WalletColumn>
      </div>
    </div>
  );
};
